import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { LogOut, ShieldCheck, User as UserIcon, ArrowLeft } from "lucide-react";

const LAFAYETTE_LOGO = "https://customer-assets.emergentagent.com/job_2af900fb-a4c0-4108-9990-fef980765d9c/artifacts/gr6pak3y_LAFAYETTE_LOGO-04.jpg";

export default function Header() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-40 border-b backdrop-blur-xl"
      style={{ background: "rgba(247, 244, 238, 0.92)", borderColor: "var(--c-border)" }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-2 sm:gap-4">
        {/* Left: Return to Lafayette + brand */}
        <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
          <a
            href="https://collegelyceeprivenice.com"
            data-testid="back-to-lafayette"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:flex items-center gap-2 text-xs transition-colors hover:text-slate-900 shrink-0"
            style={{ color: "var(--c-text-soft)" }}
            title="Lafayette International School"
          >
            <ArrowLeft size={12}/>
            <span className="overline" style={{ letterSpacing: "0.18em" }}>{t("nav.backToLafayette")}</span>
          </a>
          <div className="hidden md:block w-px h-7 shrink-0" style={{ background: "var(--c-border)" }}/>
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img
              src={LAFAYETTE_LOGO}
              alt="Lafayette International School"
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-md object-contain bg-white border shrink-0"
              style={{ borderColor: "var(--c-border)" }}
            />
            <div className="flex flex-col leading-tight min-w-0">
              <span className="font-display font-bold text-sm sm:text-base truncate" style={{ color: "var(--c-primary)" }}>
                <span className="sm:hidden">MyLafayette</span>
                <span className="hidden sm:inline">MyLafayette Admissions</span>
              </span>
              <span className="overline hidden sm:inline" style={{ color: "var(--c-text-soft)" }}>Lafayette International School</span>
            </div>
          </Link>
        </div>

        {/* Right: nav */}
        <nav className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <LanguageSwitcher />
          {user ? (
            <>
              <span className="hidden lg:flex items-center gap-2 text-sm" style={{ color: "var(--c-text-soft)" }}>
                <UserIcon size={14} /> {user.name}
              </span>
              {user.role === "admin" ? (
                <Link to="/admin" data-testid="nav-admin" className="btn-ghost text-sm">
                  <ShieldCheck size={14}/> <span className="hidden sm:inline">{t("nav.admin")}</span>
                </Link>
              ) : (
                <Link to="/dashboard" data-testid="nav-dashboard" className="btn-ghost text-xs sm:text-sm px-3 sm:px-5">
                  {t("nav.myDossier")}
                </Link>
              )}
              <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="btn-primary text-xs sm:text-sm px-3 sm:px-5">
                <LogOut size={14}/> <span className="hidden sm:inline">{t("nav.logout")}</span>
              </button>
            </>
          ) : (
            <>
              <Link
                to="/contact"
                data-testid="nav-contact"
                className="btn-ghost text-sm hidden md:inline-flex"
              >
                {t("nav.contact")}
              </Link>
              <Link to="/login" data-testid="nav-login" className="btn-cta text-xs sm:text-sm px-3 sm:px-5">{t("nav.login")}</Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
